import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/shadcn/dropdown-menu'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { RestSchemas, useRestClient } from '@/contexts/QueryProvider'
import { IconDeleteOutline } from '@icons/IconDeleteOutline'
import { IconEditOutline } from '@icons/IconEditOutline'
import { IconMenuOutline } from '@icons/IconMenuOutline'
import { IconRefreshOutline } from '@icons/IconRefreshOutline'
import { Button } from '@/components/shadcn/button'
import { Input } from '@/components/shadcn/input'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/shadcn/dialog'
import { ProviderForm } from '../Forms/ProviderForm'
import { toast } from 'sonner'
import { useState } from 'react'

type ExistingRegisteredProvider = RestSchemas['Messages.Agent.ExistingRegisteredProvider']

function refreshModelsErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message) return error.message
  if (typeof error === 'string' && error.trim()) return error
  if (error && typeof error === 'object' && 'message' in error) {
    const message = (error as { message?: unknown }).message
    if (typeof message === 'string' && message.trim()) return message
  }
  return 'Failed to refresh models'
}

export function ProviderCardActionsMenu({ provider }: { provider: ExistingRegisteredProvider }) {
  const {
    getProviderRegistrationApiListProviders,
    deleteProviderRegistrationApiDeleteProvider,
    postProviderRegistrationApiRefreshModels,
  } = useRestClient()

  const invalidateProviders = () => {
    getProviderRegistrationApiListProviders.invalidateQueries(undefined)
  }

  const { mutate: deleteProvider } = deleteProviderRegistrationApiDeleteProvider.useMutation(undefined, {
    onSuccess: () => {
      invalidateProviders()
      toast.success('Provider successfully deleted')
    },
    onError: () => {
      toast.error('Error deleting provider')
    },
  })

  const [refreshOpen, setRefreshOpen] = useState(false)
  const [apiKey, setApiKey] = useState('')
  const [teamUuid, setTeamUuid] = useState('')
  const [catalogOrigin, setCatalogOrigin] = useState('')

  const closeRefresh = () => {
    setRefreshOpen(false)
    setApiKey('')
    setTeamUuid('')
    setCatalogOrigin('')
  }

  const { mutate: refreshModels, isPending: isRefreshing } = postProviderRegistrationApiRefreshModels.useMutation(
    undefined,
    {
      onSuccess: () => {
        invalidateProviders()
        closeRefresh()
        toast.success('Models refreshed from GenAI catalog')
      },
      onError: (error) => {
        toast.error(refreshModelsErrorMessage(error))
      },
    }
  )

  const handleDeleteProvider = () => {
    deleteProvider({
      path: {
        providerId: provider.id,
      },
    })
  }

  const submitRefresh = () => {
    const key = apiKey.trim()
    const team = teamUuid.trim()
    const origin = catalogOrigin.trim()
    if (!key) {
      toast.error('Bearer token is required')
      return
    }
    if (!team) {
      toast.error('Team UUID is required')
      return
    }
    refreshModels({
      path: { providerId: provider.id },
      body: {
        apiKey: key,
        teamUuid: team,
        ...(origin ? { catalogOrigin: origin } : {}),
      },
    })
  }

  const canSubmitRefresh = !!apiKey.trim() && !!teamUuid.trim()

  return (
    <>
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="icon" className="size-8 justify-self-end">
          <IconMenuOutline size={16} />
          <span className="sr-only">Open menu</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        onCloseAutoFocus={(e) => {
          e.preventDefault()
        }}
      >
        <DropdownMenuLabel>Actions</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <ProviderForm
          selectedProvider={provider}
          sheetTitle="Edit Provider"
          sheetDescription="Edit the provider"
          onSubmit={invalidateProviders}
          sheetTrigger={
            <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
              <div className="flex items-center gap-2">
                <IconEditOutline size={16} />
                <span>Edit provider</span>
              </div>
            </DropdownMenuItem>
          }
        />
        <DropdownMenuItem
          disabled={isRefreshing || !provider.endpoint}
          onSelect={(e) => {
            e.preventDefault()
            setRefreshOpen(true)
          }}
        >
          <div className="flex items-center gap-2">
            <IconRefreshOutline size={16} />
            <span>{isRefreshing ? 'Refreshing…' : 'Refresh models'}</span>
          </div>
        </DropdownMenuItem>
        <ConfirmDialog
          title="Confirm Delete"
          description="Are you sure you want to delete this provider?"
          onConfirm={handleDeleteProvider}
          confirmVariant="destructive"
          trigger={
            <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
              <div className="flex items-center gap-2">
                <IconDeleteOutline size={16} />
                <span>Delete provider</span>
              </div>
            </DropdownMenuItem>
          }
        />
      </DropdownMenuContent>
    </DropdownMenu>
    <Dialog open={refreshOpen} onOpenChange={(open) => (open ? setRefreshOpen(true) : closeRefresh())}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Refresh models</DialogTitle>
          <DialogDescription>
            Paste the GenAI frontend bearer token and team UUID from the portal that issued the
            token. Set catalog origin when that portal host differs from the provider endpoint
            region (for example Americas portal with an EMEA chat endpoint). Values are used once
            and not stored.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <Input
            type="password"
            autoComplete="off"
            placeholder="Bearer token"
            value={apiKey}
            onChange={(event) => setApiKey(event.target.value)}
          />
          <Input
            type="text"
            autoComplete="off"
            placeholder="Team UUID"
            value={teamUuid}
            onChange={(event) => setTeamUuid(event.target.value)}
          />
          <Input
            type="text"
            autoComplete="off"
            placeholder="Catalog origin (optional)"
            value={catalogOrigin}
            onChange={(event) => setCatalogOrigin(event.target.value)}
          />
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={closeRefresh} disabled={isRefreshing}>
            Cancel
          </Button>
          <Button type="button" onClick={submitRefresh} disabled={isRefreshing || !canSubmitRefresh}>
            {isRefreshing ? 'Refreshing…' : 'Refresh'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  )
}
